import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import ButtonWithProgress from 'reusable/mui/progress-button/ButtonWithProgress'
import { observer } from 'mobx-react-lite'
import useStore from 'reusable/mobx-store-utils'
import { themeConfig } from '@gap/shared/theme'
import ReplayOutlined from '@mui/icons-material/ReplayOutlined'
import AuthCardWrapper from 'auth/shared/AuthCardWrapper'
import { useAuthStore } from 'auth/AuthStore'
import CenterFullScreen from 'reusable/mui/utilities/CenterFullScreen'
import TextFieldVerification from 'reusable/mui/text-field-verification/TextFieldVerification'
import AuthVerificationStore from './AuthVerificationStore'
import { ConfirmSignInOutput } from 'aws-amplify/auth'

export interface AuthVerificationProps {
   /** Needed for resend code functionality */
   loginFormFields?: {
      password: string
   } & ({ username: string } | { email: string })
   method: 'email' | 'totp'
   /** Callback on verify with verify result. Not used when `customHandlers.verifyCode` owns the submit. */
   onVerify?: (verifyOutput: ConfirmSignInOutput) => Promise<void>
   email?: string
   customHandlers?: {
      resendCode: () => Promise<void>
      backToLogin: () => Promise<void>
      /** Replaces the default `confirmSignIn` submit, for codes that verify an attribute rather than a sign-in challenge. */
      verifyCode?: (params: { confirmationCode: string }) => Promise<void>
   }
}

export default observer(function AuthVerification({ loginFormFields, customHandlers, email, onVerify, method }: AuthVerificationProps) {
   const authStore = useAuthStore()
   const authVerificationStore = useStore(sp => new AuthVerificationStore(sp), {
      authStore,
      loginFormFields,
      onVerify,
      customHandlers,
      email,
      method
   })

   return <CenterFullScreen>
      <AuthCardWrapper
         {...method === 'email' ? {
            title: 'Verification Code',
            subtitle: `We sent a 6-digit confirmation code to ${authVerificationStore.email}. Please enter it below to verify your email address.`
         } : {
            title: 'Enter your one-time passcode',
            subtitle: 'Please enter the 6-digit code from your authenticator app to verify your account.'
         }}
      >
         {authVerificationStore.formHandler.confirmationCode.bindInput(ip => <TextFieldVerification {...ip} onComplete={authVerificationStore.verifyCode} autoFocus />)}

         <ButtonWithProgress
            loading={authVerificationStore.loader.isLoading}
            variant="contained"
            fullWidth
            onClick={authVerificationStore.verifyCode}
            disabled={!authVerificationStore.canClickVerify}
            sx={{ whiteSpace: 'nowrap', alignSelf: 'flex-start', mt: 3 }}
         >
            Verify
         </ButtonWithProgress>

         {method === 'email' && <>
            <Divider sx={{ my: 3 }} />

            <Box display="flex" alignItems="center">
               <Typography variant="body2" sx={{ color: themeConfig.primary[600] }}>
                  Didn't receive an email?
               </Typography>
               <ButtonWithProgress
                  loading={authVerificationStore.resendCodeLoader.isLoading}
                  variant="text"
                  onClick={authVerificationStore.resendCode}
                  icon={<ReplayOutlined />}
                  iconAndLoadingPosition="end"
                  size="small"
                  sx={{ mx: 1 }}
               >
                  Resend Code
               </ButtonWithProgress>
               {authVerificationStore.canGoBack && <>
                  <Typography variant="body2" sx={{ color: themeConfig.primary[600] }}>
                     or
                  </Typography>
                  <ButtonWithProgress
                     variant="text"
                     onClick={authVerificationStore.backToLogin}
                     size="small"
                     sx={{ ml: 1 }}
                  >
                     Back to log in
                  </ButtonWithProgress>
               </>}
            </Box>
         </>}

         {method === 'totp' && authVerificationStore.canGoBack && <>
            <Divider sx={{ my: 3 }} />

            <Box display="flex" alignItems="center">
               <ButtonWithProgress
                  variant="text"
                  onClick={authVerificationStore.backToLogin}
                  size="small"
               >
                  Back to log in
               </ButtonWithProgress>
            </Box>
         </>}

      </AuthCardWrapper>
   </CenterFullScreen>
})
